import React, { useState } from 'react'
import { BLOCK_TYPE_GROUPS, PREMIUM_BLOCK_TYPES, type BlockType, type Variable } from '../types'
import type { UserPlan } from '../EditorApp'
import { TEMPLATES, type BlockTemplate } from '../defaults'
import { Type, SlidersHorizontal, BarChart3, MousePointer, ImageIcon, ListOrdered, MessageCircle, Code, LayoutTemplate, ToggleRight, ChevronDown, Minus, Play, PanelTop, Calculator, Plus, Pencil, Trash2, ChevronRight, Hash, Columns, Vote, CircleHelp, Crown } from 'lucide-react'
import FormulaEditor from './FormulaEditor'

const ICONS: Record<BlockType, React.ReactNode> = {
  text: <Type className="h-5 w-5" />,
  slider: <SlidersHorizontal className="h-5 w-5" />,
  chart: <BarChart3 className="h-5 w-5" />,
  button: <MousePointer className="h-5 w-5" />,
  image: <ImageIcon className="h-5 w-5" />,
  steps: <ListOrdered className="h-5 w-5" />,
  chat_bubble: <MessageCircle className="h-5 w-5" />,
  codeblock: <Code className="h-5 w-5" />,
  toggle: <ToggleRight className="h-5 w-5" />,
  dropdown: <ChevronDown className="h-5 w-5" />,
  divider: <Minus className="h-5 w-5" />,
  video: <Play className="h-5 w-5" />,
  tabs: <PanelTop className="h-5 w-5" />,
  number_input: <Hash className="h-5 w-5" />,
  compare: <Columns className="h-5 w-5" />,
  poll: <Vote className="h-5 w-5" />,
  quiz: <CircleHelp className="h-5 w-5" />,
}

interface BlockLibraryProps {
  onAddBlock: (type: BlockType) => void
  onAddTemplate: (template: BlockTemplate) => void
  variables?: Variable[]
  variableValues?: Record<string, number | string | boolean>
  onAddFormulaVariable?: (name: string, formula: string) => void
  onUpdateFormulaVariable?: (name: string, formula: string) => void
  onRemoveFormulaVariable?: (name: string) => void
  userPlan?: UserPlan
}

export default function BlockLibrary({ onAddBlock, onAddTemplate, variables = [], variableValues = {}, onAddFormulaVariable, onUpdateFormulaVariable, onRemoveFormulaVariable, userPlan = 'free' }: BlockLibraryProps) {
  const [showFormulaEditor, setShowFormulaEditor] = useState(false)
  const [editingFormula, setEditingFormula] = useState<string | null>(null)
  const [variablesOpen, setVariablesOpen] = useState(false)
  return (
    <aside className="w-64 border-r border-slate-200 bg-white flex flex-col shrink-0">
      <div className="p-5 border-b border-slate-100">
        <h2 className="text-sm font-bold text-slate-900">Block Library</h2>
        <p className="text-xs text-slate-500 mt-1">Drag components to canvas</p>
      </div>

      <div className="p-3 space-y-1 overflow-y-auto flex-1">
        {BLOCK_TYPE_GROUPS.map((group) => (
          <div key={group.label}>
            <span className="px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">{group.label}</span>
            <div className="mt-1 mb-2 space-y-0.5">
              {group.types.map(({ type, label }) => (
                <button
                  key={type}
                  onClick={() => onAddBlock(type)}
                  className="group w-full flex items-center gap-3 p-2.5 rounded-xl border border-transparent hover:border-[#5048e5]/20 hover:bg-[#5048e5]/5 cursor-grab transition-all text-left"
                >
                  <span className="text-slate-400 group-hover:text-[#5048e5]">{ICONS[type]}</span>
                  <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">{label}</span>
                  {userPlan === 'free' && PREMIUM_BLOCK_TYPES.has(type) && (
                    <Crown className="h-3.5 w-3.5 text-amber-400 ml-auto shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div className="pt-3 mt-3 border-t border-slate-100">
          <span className="px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">Templates</span>
          <div className="mt-2 space-y-1">
            {TEMPLATES.map((tpl) => (
              <button
                key={tpl.name}
                onClick={() => onAddTemplate(tpl)}
                className="group w-full flex items-center gap-3 p-3 rounded-xl border border-transparent hover:border-[#5048e5]/20 hover:bg-[#5048e5]/5 transition-all text-left"
              >
                <span className="text-slate-400 group-hover:text-[#5048e5]">
                  <LayoutTemplate className="h-5 w-5" />
                </span>
                <div>
                  <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 block">{tpl.name}</span>
                  <span className="text-[11px] text-slate-400">{tpl.description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Variables section */}
      {variables.length > 0 && (
        <div className="px-3 py-2 border-t border-slate-100">
          <button
            onClick={() => setVariablesOpen(!variablesOpen)}
            className="flex items-center gap-1.5 px-3 w-full text-left"
          >
            <ChevronRight className={`h-3 w-3 text-slate-400 transition-transform ${variablesOpen ? 'rotate-90' : ''}`} />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Variables</span>
            <span className="text-[10px] text-slate-300 ml-auto">{variables.length}</span>
          </button>
          {variablesOpen && <div className="mt-2 space-y-0.5 max-h-40 overflow-y-auto">
            {variables.map((v) => (
              <div key={v.name} className="group flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg hover:bg-slate-50">
                {v.type === 'formula' ? (
                  <Calculator className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                ) : (
                  <SlidersHorizontal className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-slate-600 truncate block">@{v.name}</span>
                  {v.type === 'formula' && v.formula && (
                    <span className="text-[10px] text-slate-400 truncate block">{v.formula}</span>
                  )}
                </div>
                {v.type === 'formula' ? (
                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      onClick={() => setEditingFormula(v.name)}
                      className="p-1 text-slate-300 hover:text-[#5048e5] transition-colors"
                      title="Edit formula"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => onRemoveFormulaVariable?.(v.name)}
                      className="p-1 text-slate-300 hover:text-red-400 transition-colors"
                      title="Delete formula"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <span className="text-[10px] text-slate-300 shrink-0">{v.type}</span>
                )}
              </div>
            ))}
          </div>}
        </div>
      )}

      {/* Formula creation / editing */}
      <div className="px-3 pb-2">
        {editingFormula ? (() => {
          const v = variables.find((vr) => vr.name === editingFormula)
          return (
            <FormulaEditor
              variables={variables.filter((vr) => vr.name !== editingFormula)}
              variableValues={variableValues}
              onSubmit={(_, formula) => {
                onUpdateFormulaVariable?.(editingFormula, formula)
                setEditingFormula(null)
              }}
              onCancel={() => setEditingFormula(null)}
              editMode
              initialName={editingFormula}
              initialFormula={v?.formula || ''}
            />
          )
        })() : showFormulaEditor ? (
          <FormulaEditor
            variables={variables}
            variableValues={variableValues}
            onSubmit={(name, formula) => {
              onAddFormulaVariable?.(name, formula)
              setShowFormulaEditor(false)
            }}
            onCancel={() => setShowFormulaEditor(false)}
          />
        ) : (
          <button
            onClick={() => setShowFormulaEditor(true)}
            className="w-full flex items-center justify-center gap-1.5 p-2.5 rounded-xl border border-dashed border-slate-200 hover:border-[#5048e5]/30 hover:bg-[#5048e5]/5 text-xs font-medium text-slate-400 hover:text-[#5048e5] transition-all"
          >
            <Plus className="h-3.5 w-3.5" />
            Formula Variable
          </button>
        )}
      </div>

    </aside>
  )
}
